import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR, formatDate } from "@/lib/constants";
import { LeadFormModal } from "@/components/LeadFormModal";
import { colDef, buildCfLabels } from "@/lib/leadColumns";
import { LogOut, Wallet, TrendingUp, Clock, CheckCircle2, Headphones, ShieldCheck, Plus, UserPlus, UserCircle, Menu, X, Gift } from "lucide-react";
import { LogoMark } from "@/components/Logo";

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <div data-testid={`portal-kpi-${label.replace(/\s+/g, "-").toLowerCase()}`} className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-1 ${accent}`} />
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</div>
          <div className="text-2xl font-extrabold text-slate-900 font-heading mt-2">{value}</div>
        </div>
        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600"><Icon size={20} /></div>
      </div>
    </div>
  );
}

const NAV = [
  { key: "leads", label: "My Leads", icon: UserPlus },
  { key: "commissions", label: "My Payouts", icon: Wallet },
  { key: "offers", label: "Offers", icon: Gift },
  { key: "profile", label: "My Profile", icon: UserCircle },
];

export default function PartnerPortal() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("leads");
  const [showAdd, setShowAdd] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [lenders, setLenders] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [leadCols, setLeadCols] = useState([]);
  const [offers, setOffers] = useState([]);
  const [activeOffer, setActiveOffer] = useState(null);

  const loadSummary = () => { if (user?.role === "partner") api.get("/partner/summary").then((r) => setData(r.data)).catch(() => {}); };
  useEffect(() => { loadSummary(); /* eslint-disable-next-line */ }, [user]);
  useEffect(() => {
    if (user?.role !== "partner") return;
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/lenders").then((r) => setLenders(r.data)).catch(() => {});
    api.get("/settings/lead").then((r) => { setDocTypes(r.data.doc_types || []); setLeadCols(r.data.partner_columns || r.data.columns || []); }).catch(() => {});
    api.get("/offers/active").then((r) => setOffers(r.data)).catch(() => setOffers([]));
  }, [user]);

  if (user && user.role !== "partner") return <Navigate to="/dashboard" replace />;

  const p = data?.partner;
  const s = data?.stats;
  const cfLabels = buildCfLabels(products);
  const cd = (k) => colDef(k, cfLabels);
  const activeCols = (leadCols || []).filter((k) => cd(k));
  const partnerTypeLabel = (p?.partner_type || "partner").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const SidebarContent = () => (
    <>
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="px-6 py-5 flex items-center gap-3 border-b border-slate-800">
          <LogoMark className="w-9 h-9" />
          <div>
            <div className="text-white font-bold font-heading leading-tight">Arjun Fintech</div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-mono">Partner Portal</div>
          </div>
        </div>
        <nav className="px-3 py-4 space-y-1 flex-1 overflow-y-auto">
          {NAV.map((n) => (
            <button
              key={n.key}
              data-testid={`portal-nav-${n.key}`}
              onClick={() => { setTab(n.key); setMobileOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                tab === n.key
                  ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/20"
                  : "text-slate-400 hover:text-white hover:bg-slate-800/60 border border-transparent"
              }`}
            >
              <n.icon size={18} />
              {n.label}
            </button>
          ))}
        </nav>
      </div>
      <div className="px-3 py-4 border-t border-slate-800">
        <div className="px-3 py-2">
          <div className="text-sm font-semibold text-white truncate" data-testid="sidebar-user-name">{p?.name || user?.name}</div>
          <div className="text-xs text-emerald-400 font-mono">{partnerTypeLabel}</div>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="hidden lg:flex w-64 bg-slate-900 flex-col justify-between fixed inset-y-0 left-0 z-40">
        <SidebarContent />
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-slate-900/60" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 bg-slate-900 flex flex-col justify-between">
            <button onClick={() => setMobileOpen(false)} className="absolute top-4 right-4 text-slate-400" data-testid="portal-mobile-close"><X size={20} /></button>
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-200">
          <div className="h-16 px-4 sm:px-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button className="lg:hidden p-2 -ml-2 text-slate-600" onClick={() => setMobileOpen(true)} data-testid="portal-mobile-menu">
                <Menu size={22} />
              </button>
              <div className="text-sm text-slate-500 font-mono hidden sm:block">Partner Operations</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <div className="text-sm font-semibold text-slate-800 leading-tight" data-testid="portal-header-user-name">{p?.name || user?.name}</div>
                <div className="text-[11px] text-emerald-600 font-mono">{partnerTypeLabel}</div>
              </div>
              <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-semibold text-sm">
                {(p?.name || user?.name || "?").charAt(0).toUpperCase()}
              </div>
              <button data-testid="portal-logout-button" onClick={logout} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 hover:text-rose-600 hover:bg-rose-50 border border-slate-200 transition-all">
                <LogOut size={16} /> <span className="hidden sm:inline">Sign out</span>
              </button>
            </div>
          </div>
          <div className="border-t border-slate-200 bg-slate-50" data-testid="portal-sm-topbar">
            <div className="px-4 sm:px-6 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center flex-shrink-0"><Headphones size={16} /></div>
                <div className="leading-tight">
                  <div className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold">Your Sales Manager</div>
                  <div className="text-sm font-semibold text-slate-800" data-testid="portal-sm-name">{data?.sales_manager?.name || p?.sales_manager_name || "Not assigned"}</div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-slate-600 sm:ml-2">
                {data?.sales_manager?.phone && <span data-testid="portal-sm-phone" className="inline-flex items-center gap-1.5"><span className="text-slate-400">📞</span>{data.sales_manager.phone}</span>}
                {data?.sales_manager?.email && <span data-testid="portal-sm-email" className="inline-flex items-center gap-1.5"><span className="text-slate-400">✉️</span>{data.sales_manager.email}</span>}
              </div>
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto pb-24 lg:pb-8 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight">Welcome, {p?.name || user?.name}</h1>
              <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
                <ShieldCheck size={15} className="text-emerald-500" /> KYC {p?.kyc_status || "pending"} · {partnerTypeLabel}
              </p>
            </div>
            {tab === "leads" && (
              <button data-testid="portal-new-lead-button" onClick={() => setShowAdd(true)} className="self-start sm:self-auto px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium text-sm flex items-center gap-2 shadow-sm">
                <Plus size={16} /> New Lead
              </button>
            )}
          </div>

          {s && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi label="Total Leads" value={s.total_leads} icon={TrendingUp} accent="bg-gradient-to-r from-slate-500 to-slate-700" />
              <Kpi label="In Progress" value={s.in_progress} icon={Clock} accent="bg-gradient-to-r from-blue-500 to-indigo-600" />
              <Kpi label="Disbursed" value={s.disbursed} icon={CheckCircle2} accent="bg-gradient-to-r from-emerald-500 to-emerald-700" />
              <Kpi label="Total Payout" value={formatINR(s.total_payout)} icon={Wallet} accent="bg-gradient-to-r from-amber-500 to-orange-600" />
            </div>
          )}

          {tab === "leads" && (
            <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    {activeCols.map((k) => <th key={k} className={`px-4 py-3 ${cd(k).align === "right" ? "text-right" : "text-left"}`}>{cd(k).label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {(data?.leads || []).map((l) => (
                    <tr key={l.id} className="border-b border-slate-100 text-sm text-slate-700">
                      {activeCols.map((k) => <td key={k} className={`px-4 py-3 ${cd(k).align === "right" ? "text-right" : ""}`}>{cd(k).cell(l)}</td>)}
                    </tr>
                  ))}
                  {(!data?.leads || data.leads.length === 0) && <tr><td colSpan={activeCols.length || 1} className="px-4 py-12 text-center text-slate-400 text-sm">No leads yet.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === "commissions" && (
            <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm overflow-x-auto">
              <table className="w-full min-w-[860px]">
                <thead>
                  <tr className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="text-left px-4 py-3">Customer</th>
                    <th className="text-right px-4 py-3">Disbursed</th>
                    <th className="text-right px-4 py-3">Gross</th>
                    <th className="text-right px-4 py-3">Incentive</th>
                    <th className="text-right px-4 py-3">TDS</th>
                    <th className="text-right px-4 py-3">Net Payout</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Payment Ref.</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.commissions || []).map((c) => (
                    <tr key={c.id} data-testid={`portal-payout-row-${c.id}`} className="border-b border-slate-100 text-sm text-slate-700">
                      <td className="px-4 py-3 font-medium text-slate-900">{c.customer_name}</td>
                      <td className="px-4 py-3 text-right font-mono">{formatINR(c.disbursed_amount)}</td>
                      <td className="px-4 py-3 text-right font-mono">{formatINR(c.gross_commission)}</td>
                      <td className="px-4 py-3 text-right font-mono text-indigo-600">{c.incentive ? `+${formatINR(c.incentive)}` : "—"}</td>
                      <td className="px-4 py-3 text-right font-mono text-rose-600">{c.tds_amount ? `-${formatINR(c.tds_amount)}` : "—"}</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-600">{formatINR(c.net_payout)}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${c.status === "settled" ? "bg-emerald-100 text-emerald-700 border-emerald-300" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{c.status}</span></td>
                      <td className="px-4 py-3 text-xs">
                        {c.status === "settled" ? (
                          <div>
                            <div className="font-medium text-slate-800 whitespace-pre-wrap" data-testid={`portal-payout-settle-note-${c.id}`}>{c.settle_note || <span className="text-slate-400">—</span>}</div>
                            <div className="text-[10px] text-slate-400">{c.settled_at ? formatDate(c.settled_at) : ""}{c.paid_amount ? ` · Paid ${formatINR(c.paid_amount)}` : ""}</div>
                          </div>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                    </tr>
                  ))}
                  {(!data?.commissions || data.commissions.length === 0) && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400 text-sm">No payouts yet.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === "offers" && (
            offers.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500 text-sm" data-testid="portal-offers-empty">No active offers right now. Please check back later.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {offers.map((o) => (
                  <button key={o.id} data-testid={`portal-offer-card-${o.id}`} onClick={() => setActiveOffer(o)} className="text-left rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-md hover:border-emerald-300 transition-all flex flex-col">
                    <div className="aspect-[16/9] bg-slate-100 overflow-hidden flex items-center justify-center">
                      {o.image_data ? <img src={o.image_data} alt={o.title} className="w-full h-full object-cover" /> : <Gift size={36} className="text-slate-300" />}
                    </div>
                    <div className="p-4 flex-1 flex flex-col gap-2">
                      <div className="font-semibold text-slate-900">{o.title}</div>
                      {o.description && <div className="text-sm text-slate-600 whitespace-pre-line line-clamp-3">{o.description}</div>}
                    </div>
                  </button>
                ))}
              </div>
            )
          )}

          {tab === "profile" && p && (
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 max-w-3xl">
              {[["Name", p.name], ["Email", p.email], ["Phone", p.phone], ["Partner Type", partnerTypeLabel],
                ["Business Type", p.business_type], ["City", p.city], ["State", p.state], ["PAN", p.pan], ["GST", p.gst],
                ["Bank", p.bank_name], ["Account", p.account_number], ["IFSC", p.ifsc],
                ["Sales Manager", data?.sales_manager?.name || p.sales_manager_name],
                ["SM Mobile", data?.sales_manager?.phone], ["SM Email", data?.sales_manager?.email]].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-slate-100 pb-2">
                  <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{k}</span>
                  <span className="text-sm text-slate-800 font-medium text-right">{v || "—"}</span>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-slate-900 border-t border-slate-800 flex items-stretch justify-around pb-[env(safe-area-inset-bottom)]" data-testid="portal-bottom-nav">
        {NAV.map((n) => (
          <button
            key={n.key}
            data-testid={`portal-bottomnav-${n.key}`}
            onClick={() => setTab(n.key)}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium transition-colors ${
              tab === n.key ? "text-emerald-400" : "text-slate-400"
            }`}
          >
            <n.icon size={20} />
            <span className="truncate max-w-full px-1">{n.label}</span>
          </button>
        ))}
      </nav>

      {p && (
        <LeadFormModal open={showAdd} onClose={() => setShowAdd(false)} onCreated={loadSummary}
          isPartner partnerId={p.id} products={products} lenders={lenders} docTypes={docTypes} />
      )}

      {activeOffer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" onClick={() => setActiveOffer(null)} data-testid="portal-offer-detail-overlay">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            {activeOffer.image_data && (
              <div className="w-full aspect-[16/9] bg-slate-100 overflow-hidden flex items-center justify-center">
                <img src={activeOffer.image_data} alt={activeOffer.title} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-6 space-y-4 overflow-y-auto">
              <h2 className="text-xl font-extrabold text-slate-900">{activeOffer.title}</h2>
              {activeOffer.description && <div className="text-sm text-slate-700 whitespace-pre-line">{activeOffer.description}</div>}
              {activeOffer.terms_and_conditions && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Terms & Conditions</div>
                  <div className="text-xs text-slate-600 whitespace-pre-line">{activeOffer.terms_and_conditions}</div>
                </div>
              )}
              <div className="flex justify-end pt-2">
                <button onClick={() => setActiveOffer(null)} data-testid="portal-offer-detail-close" className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
